import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface CurrencySettings {
  symbol: string;
  precision: number;
  text: string;
  position: 'before' | 'after';
  thousandsSeparator: string;
  decimalSeparator: string;
}

export interface CurrencySettingsResponse {
  success: boolean;
  data: CurrencySettings;
  message?: string;
}

class CurrencySettingsService {
  private apiRoot = configs.apiRoot;

  /**
   * Get currency settings for current tenant
   */
  async getSettings(): Promise<CurrencySettings> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/currency-settings`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      // If 404, return default settings (settings don't exist yet)
      if (response.status === 404) {
        return {
          symbol: '₫',
          precision: 0,
          text: 'VND',
          position: 'after',
          thousandsSeparator: ',',
          decimalSeparator: '.',
        };
      }
      
      // Try to parse error message if available
      try {
        const error = await response.json();
        throw new Error(
          error.message || `Failed to fetch currency settings: ${response.statusText}`
        );
      } catch (e) {
        throw new Error(`Failed to fetch currency settings: ${response.statusText}`);
      }
    }

    const result: CurrencySettingsResponse = await response.json();
    return result.data;
  }

  /**
   * Update currency settings for current tenant
   */
  async updateSettings(
    updates: Partial<CurrencySettings>
  ): Promise<CurrencySettings> {
    const response = await authenticatedFetch(
      `${this.apiRoot}/currency-settings`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to update currency settings: ${response.statusText}`
      );
    }

    const result: CurrencySettingsResponse = await response.json();
    return result.data;
  }
}

export const currencySettingsService = new CurrencySettingsService();

